/**
 * 实时同步管理器
 * 统一管理 WebSocket、SSE 和 GraphQL 订阅的实时同步机制
 */

import { EventEmitter } from 'events';
import { WebSocketServer, WebSocket } from 'ws';
import { PubSub } from 'graphql-subscriptions';
import { logger } from '../utils/logger.js';
import { TaskStore } from './task-store.js';
import { WorkflowEngineV2 } from './workflow-engine-v2.js';
import { AgentManager } from './agent-manager.js';
import type { UnifiedTask } from '../types/task.js';
import type { WorkflowExecutionUpdate } from '../types/workflow-v2.js';

// 事件类型
export enum RealtimeEventType {
  // 任务事件
  TASK_CREATED = 'task.created',
  TASK_UPDATED = 'task.updated',
  TASK_DELETED = 'task.deleted',
  TASK_STATUS_CHANGED = 'task.status_changed',
  TASK_ASSIGNED = 'task.assigned',
  
  // 工作流事件
  WORKFLOW_STARTED = 'workflow.started',
  WORKFLOW_NODE_EXECUTED = 'workflow.node_executed',
  WORKFLOW_COMPLETED = 'workflow.completed',
  WORKFLOW_FAILED = 'workflow.failed',
  
  // Agent 事件
  AGENT_ASSIGNED = 'agent.assigned',
  AGENT_STARTED = 'agent.started',
  AGENT_COMPLETED = 'agent.completed',
  AGENT_FAILED = 'agent.failed',
  
  // 系统事件
  SYSTEM_NOTIFICATION = 'system.notification',
  SYSTEM_ERROR = 'system.error',
  
  // 连接事件
  CLIENT_CONNECTED = 'client.connected',
  CLIENT_DISCONNECTED = 'client.disconnected',
}

// 实时事件数据
export interface RealtimeEvent {
  id: string;
  type: RealtimeEventType;
  timestamp: string;
  data: any;
  source?: string;
  userId?: string;
  projectId?: string;
}

// 客户端信息
export interface RealtimeClient {
  id: string;
  type: 'websocket' | 'sse' | 'graphql';
  connection: WebSocket | Response | any;
  userId?: string;
  projectId?: string;
  subscribedEvents: RealtimeEventType[];
  metadata?: Record<string, any>;
}

// 同步配置
export interface RealtimeSyncConfig {
  enableWebSocket?: boolean;
  enableSSE?: boolean;
  enableGraphQL?: boolean;
  heartbeatInterval?: number;
  maxClients?: number;
  authRequired?: boolean;
}

export class RealtimeSyncManager extends EventEmitter {
  private static instance: RealtimeSyncManager;
  private clients: Map<string, RealtimeClient> = new Map();
  private pubsub: PubSub;
  private wsServer?: WebSocketServer;
  private sseClients: Map<string, Response> = new Map();
  private config: RealtimeSyncConfig;
  private heartbeatTimer?: NodeJS.Timer;
  private taskStore: TaskStore;
  private workflowEngine: WorkflowEngineV2;
  private agentManager: AgentManager;
  
  private constructor(config: RealtimeSyncConfig = {}) {
    super();
    this.config = {
      enableWebSocket: true,
      enableSSE: true,
      enableGraphQL: true,
      heartbeatInterval: 30000, // 30秒
      maxClients: 1000,
      authRequired: false,
      ...config
    };
    
    this.pubsub = new PubSub();
    this.taskStore = new TaskStore();
    this.workflowEngine = new WorkflowEngineV2();
    this.agentManager = new AgentManager();
    this.setupEventListeners();
    this.startHeartbeat();
  }
  
  static getInstance(config?: RealtimeSyncConfig): RealtimeSyncManager {
    if (!RealtimeSyncManager.instance) {
      RealtimeSyncManager.instance = new RealtimeSyncManager(config);
    }
    return RealtimeSyncManager.instance;
  }
  
  /**
   * 设置事件监听器
   */
  private setupEventListeners(): void {
    // 监听任务存储事件
    // TODO: TaskStore 需要继承 EventEmitter 才能支持事件监听
    // const taskStore = this.taskStore;
    // taskStore.on('task:created', (task: UnifiedTask) => {
    //   this.broadcastEvent({
    //     id: `evt_${Date.now()}`,
    //     type: RealtimeEventType.TASK_CREATED,
    //     timestamp: new Date().toISOString(),
    //     data: { task }
    //   });
    // });
    
    // taskStore.on('task:updated', (task: UnifiedTask, changes: any) => {
    //   this.broadcastEvent({
    //     id: `evt_${Date.now()}`,
    //     type: RealtimeEventType.TASK_UPDATED,
    //     timestamp: new Date().toISOString(),
    //     data: { task, changes }
    //   });
      
    //   // 如果状态改变，发送特定事件
    //   if (changes.status) {
    //     this.broadcastEvent({
    //       id: `evt_${Date.now()}`,
    //       type: RealtimeEventType.TASK_STATUS_CHANGED,
    //       timestamp: new Date().toISOString(),
    //       data: { 
    //         taskId: task.id,
    //         oldStatus: changes.status.old,
    //         newStatus: changes.status.new 
    //       }
    //     });
    //   }
    // });
    
    // taskStore.on('task:deleted', (taskId: string) => {
    //   this.broadcastEvent({
    //     id: `evt_${Date.now()}`,
    //     type: RealtimeEventType.TASK_DELETED,
    //     timestamp: new Date().toISOString(),
    //     data: { taskId }
    //   });
    // });
    
    // 监听工作流引擎事件
    const workflowEngine = this.workflowEngine;
    workflowEngine.on('execution:started', (data: any) => {
      this.broadcastEvent({
        id: `evt_${Date.now()}`,
        type: RealtimeEventType.WORKFLOW_STARTED,
        timestamp: new Date().toISOString(),
        data
      });
    });
    
    workflowEngine.on('node:executed', (data: any) => {
      this.broadcastEvent({
        id: `evt_${Date.now()}`,
        type: RealtimeEventType.WORKFLOW_NODE_EXECUTED,
        timestamp: new Date().toISOString(),
        data
      });
    });
    
    workflowEngine.on('execution:completed', (data: any) => {
      this.broadcastEvent({
        id: `evt_${Date.now()}`,
        type: RealtimeEventType.WORKFLOW_COMPLETED,
        timestamp: new Date().toISOString(),
        data
      });
    });
    
    workflowEngine.on('execution:failed', (data: any) => {
      this.broadcastEvent({
        id: `evt_${Date.now()}`,
        type: RealtimeEventType.WORKFLOW_FAILED,
        timestamp: new Date().toISOString(),
        data
      });
    });
    
    // 监听 Agent 管理器事件
    // TODO: AgentManager 需要继承 EventEmitter 才能支持事件监听
    // const agentManager = this.agentManager;
    // agentManager.on('agent:assigned', (data: any) => {
    //   this.broadcastEvent({
    //     id: `evt_${Date.now()}`,
    //     type: RealtimeEventType.AGENT_ASSIGNED,
    //     timestamp: new Date().toISOString(),
    //     data
    //   });
    // });
    
    // agentManager.on('agent:started', (data: any) => {
    //   this.broadcastEvent({
    //     id: `evt_${Date.now()}`,
    //     type: RealtimeEventType.AGENT_STARTED,
    //     timestamp: new Date().toISOString(),
    //     data
    //   });
    // });
    
    // agentManager.on('agent:completed', (data: any) => {
    //   this.broadcastEvent({
    //     id: `evt_${Date.now()}`,
    //     type: RealtimeEventType.AGENT_COMPLETED,
    //     timestamp: new Date().toISOString(),
    //     data
    //   });
    // });
    
    // agentManager.on('agent:failed', (data: any) => {
    //   this.broadcastEvent({
    //     id: `evt_${Date.now()}`,
    //     type: RealtimeEventType.AGENT_FAILED,
    //     timestamp: new Date().toISOString(),
    //     data
    //   });
    // });
  }
  
  /**
   * 启动心跳检测
   */
  private startHeartbeat(): void {
    if (this.config.heartbeatInterval && this.config.heartbeatInterval > 0) {
      this.heartbeatTimer = setInterval(() => {
        this.sendHeartbeat();
      }, this.config.heartbeatInterval);
    }
  }
  
  /**
   * 发送心跳
   */
  private sendHeartbeat(): void {
    const heartbeatEvent: RealtimeEvent = {
      id: `hb_${Date.now()}`,
      type: RealtimeEventType.SYSTEM_NOTIFICATION,
      timestamp: new Date().toISOString(),
      data: { 
        type: 'heartbeat',
        serverTime: new Date().toISOString(),
        clientCount: this.clients.size
      }
    };
    
    this.broadcastEvent(heartbeatEvent);
  }
  
  /**
   * 设置 WebSocket 服务器
   */
  setupWebSocketServer(server: any): void {
    if (!this.config.enableWebSocket) return;
    
    this.wsServer = new WebSocketServer({ server, path: '/ws' });
    
    this.wsServer.on('connection', (ws: WebSocket, request: any) => {
      const clientId = `ws_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const client: RealtimeClient = {
        id: clientId,
        type: 'websocket',
        connection: ws,
        subscribedEvents: Object.values(RealtimeEventType),
        metadata: {
          connectedAt: new Date().toISOString(),
          userAgent: request.headers['user-agent']
        }
      };
      
      this.addClient(client);
      
      // 发送欢迎消息
      ws.send(JSON.stringify({
        type: 'connection',
        clientId,
        message: 'Connected to Workflow Realtime Sync'
      }));
      
      // 处理消息
      ws.on('message', (data: Buffer) => {
        try {
          const message = JSON.parse(data.toString());
          this.handleWebSocketMessage(clientId, message);
        } catch (error) {
          logger.error('Invalid WebSocket message:', error);
        }
      });
      
      // 处理断开连接
      ws.on('close', () => {
        this.removeClient(clientId);
      });
      
      // 处理错误
      ws.on('error', (error: Error) => {
        logger.error('WebSocket error:', error);
        this.removeClient(clientId);
      });
    });
    
    logger.info('WebSocket server setup complete');
  }
  
  /**
   * 处理 WebSocket 消息
   */
  private handleWebSocketMessage(clientId: string, message: any): void {
    const client = this.clients.get(clientId);
    if (!client) return;
    
    switch (message.type) {
      case 'subscribe':
        if (message.events && Array.isArray(message.events)) {
          client.subscribedEvents = message.events;
          this.clients.set(clientId, client);
        }
        break;
        
      case 'unsubscribe':
        if (message.events && Array.isArray(message.events)) {
          client.subscribedEvents = client.subscribedEvents.filter(
            event => !message.events.includes(event)
          );
          this.clients.set(clientId, client);
        }
        break;
        
      case 'ping':
        if (client.type === 'websocket') {
          (client.connection as WebSocket).send(JSON.stringify({
            type: 'pong',
            timestamp: new Date().toISOString()
          }));
        }
        break;
        
      default:
        // 处理自定义消息
        this.emit('client:message', { clientId, message });
    }
  }
  
  /**
   * 添加 SSE 客户端
   */
  addSSEClient(clientId: string, response: any, userId?: string, projectId?: string): void {
    if (!this.config.enableSSE) return;
    
    const client: RealtimeClient = {
      id: clientId,
      type: 'sse',
      connection: response,
      userId,
      projectId,
      subscribedEvents: Object.values(RealtimeEventType),
      metadata: {
        connectedAt: new Date().toISOString()
      }
    };
    
    this.addClient(client);
    this.sseClients.set(clientId, response);
    
    // 发送初始化事件
    this.sendSSEEvent(response, {
      type: 'connection',
      data: JSON.stringify({
        clientId,
        message: 'Connected to SSE stream'
      })
    });
  }
  
  /**
   * 移除 SSE 客户端
   */
  removeSSEClient(clientId: string): void {
    this.removeClient(clientId);
    this.sseClients.delete(clientId);
  }
  
  /**
   * 发送 SSE 事件
   */
  private sendSSEEvent(response: any, event: any): void {
    try {
      response.write(`event: ${event.type}\n`);
      response.write(`data: ${event.data}\n\n`);
    } catch (error) {
      logger.error('Failed to send SSE event:', error);
    }
  }
  
  /**
   * 添加客户端
   */
  private addClient(client: RealtimeClient): void {
    if (this.clients.size >= this.config.maxClients!) {
      logger.warn('Max clients reached, rejecting new connection');
      if (client.type === 'websocket') {
        (client.connection as WebSocket).close(1008, 'Max clients reached');
      }
      return;
    }
    
    this.clients.set(client.id, client);
    
    // 广播客户端连接事件
    this.broadcastEvent({
      id: `evt_${Date.now()}`,
      type: RealtimeEventType.CLIENT_CONNECTED,
      timestamp: new Date().toISOString(),
      data: {
        clientId: client.id,
        clientType: client.type,
        totalClients: this.clients.size
      }
    }, [client.id]); // 排除新连接的客户端
    
    logger.info(`Client connected: ${client.id} (${client.type})`);
  }
  
  /**
   * 移除客户端
   */
  private removeClient(clientId: string): void {
    const client = this.clients.get(clientId);
    if (!client) return;
    
    this.clients.delete(clientId);
    
    // 广播客户端断开连接事件
    this.broadcastEvent({
      id: `evt_${Date.now()}`,
      type: RealtimeEventType.CLIENT_DISCONNECTED,
      timestamp: new Date().toISOString(),
      data: {
        clientId: client.id,
        clientType: client.type,
        totalClients: this.clients.size
      }
    });
    
    logger.info(`Client disconnected: ${client.id} (${client.type})`);
  }
  
  /**
   * 广播事件到所有客户端
   */
  broadcastEvent(event: RealtimeEvent, excludeClients: string[] = []): void {
    // GraphQL 订阅
    if (this.config.enableGraphQL) {
      this.pubsub.publish(event.type, event);
    }
    
    // WebSocket 和 SSE 客户端
    this.clients.forEach((client, clientId) => {
      if (excludeClients.includes(clientId)) return;
      if (!client.subscribedEvents.includes(event.type)) return;
      
      try {
        switch (client.type) {
          case 'websocket':
            const ws = client.connection as WebSocket;
            if (ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify(event));
            }
            break;
            
          case 'sse':
            const sseResponse = this.sseClients.get(clientId);
            if (sseResponse) {
              this.sendSSEEvent(sseResponse, {
                type: event.type,
                data: JSON.stringify(event)
              });
            }
            break;
        }
      } catch (error) {
        logger.error(`Failed to send event to client ${clientId}:`, error);
        this.removeClient(clientId);
      }
    });
  }
  
  /**
   * 发送系统通知
   */
  sendSystemNotification(message: string, level: 'info' | 'warning' | 'error' = 'info'): void {
    this.broadcastEvent({
      id: `notify_${Date.now()}`,
      type: RealtimeEventType.SYSTEM_NOTIFICATION,
      timestamp: new Date().toISOString(),
      data: {
        message,
        level
      }
    });
  }
  
  /**
   * 获取 PubSub 实例（用于 GraphQL）
   */
  getPubSub(): PubSub {
    return this.pubsub;
  }
  
  /**
   * 获取客户端统计
   */
  getStats(): any {
    const stats = {
      totalClients: this.clients.size,
      clientsByType: {
        websocket: 0,
        sse: 0,
        graphql: 0
      },
      uptime: process.uptime(),
      memory: process.memoryUsage()
    };
    
    this.clients.forEach(client => {
      stats.clientsByType[client.type]++;
    });
    
    return stats;
  }
  
  /**
   * 停止实时同步管理器
   */
  async stop(): Promise<void> {
    // 停止心跳
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }
    
    // 断开所有客户端
    this.clients.forEach((client, clientId) => {
      if (client.type === 'websocket') {
        (client.connection as WebSocket).close(1001, 'Server shutting down');
      }
      this.removeClient(clientId);
    });
    
    // 关闭 WebSocket 服务器
    if (this.wsServer) {
      this.wsServer.close();
    }
    
    logger.info('Realtime sync manager stopped');
  }
}

// 导出便捷函数
export function getRealtimeSyncManager(config?: RealtimeSyncConfig): RealtimeSyncManager {
  return RealtimeSyncManager.getInstance(config);
}